package de.xtl.retroguide.join

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import de.xtl.retroguide.KtorRetroRemoteDataSource
import de.xtl.retroguide.RetroApiError
import de.xtl.retroguide.RetroApiResult
import de.xtl.retroguide.RetroRemoteDataSource
import de.xtl.retroguide.SessionAccessStorage
import de.xtl.retroguide.defaultSessionAccessStorage
import de.xtl.retroguide.markdownFileName
import de.xtl.retroguide.toMarkdown
import de.xtl.retroguide.protocol.MAX_DISPLAY_NAME_LENGTH
import de.xtl.retroguide.protocol.DEFAULT_QUESTION_DURATION_SECONDS
import de.xtl.retroguide.protocol.ParticipantRole
import de.xtl.retroguide.protocol.DEFAULT_VOTES_PER_PARTICIPANT
import de.xtl.retroguide.protocol.MAX_CLUSTER_NAME_LENGTH
import de.xtl.retroguide.protocol.QuestionWidget
import kotlin.random.Random
import de.xtl.retroguide.protocol.RetroPhase
import de.xtl.retroguide.protocol.SESSION_CODE_LENGTH
import de.xtl.retroguide.protocol.SessionAccess
import de.xtl.retroguide.protocol.SessionRoster
import de.xtl.retroguide.protocol.SessionSnapshot
import de.xtl.retroguide.protocol.StartRetroQuestion
import de.xtl.retroguide.serverBaseUrl
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.delay

class JoinSessionViewModel(
    initialSessionCode: String? = null,
    private val remoteDataSource: RetroRemoteDataSource = KtorRetroRemoteDataSource(serverBaseUrl),
    private val sessionAccessStorage: SessionAccessStorage = defaultSessionAccessStorage(),
) : ViewModel() {
    private val _state = MutableStateFlow(
        JoinSessionState(sessionCode = initialSessionCode.orEmpty().asSessionCode()),
    )
    val state = _state.asStateFlow()
    private val _events = Channel<JoinSessionEvent>(Channel.BUFFERED)
    val events = _events.receiveAsFlow()
    private var lobbyJob: Job? = null
    private var rosterJob: Job? = null
    private var timerJob: Job? = null

    init {
        val storedAccess = sessionAccessStorage.load()
        if (_state.value.sessionCode.isNotEmpty() && storedAccess?.sessionCode != _state.value.sessionCode) {
            sessionAccessStorage.clear()
            observeRoster(_state.value.sessionCode)
        } else {
            storedAccess?.let(::openSession) ?: observeRoster(_state.value.sessionCode)
        }
    }

    fun onAction(action: JoinSessionAction) {
        when (action) {
            is JoinSessionAction.OnDisplayNameChange -> _state.update {
                it.copy(
                    displayName = action.value.take(MAX_DISPLAY_NAME_LENGTH),
                    error = null,
                )
            }

            is JoinSessionAction.OnSessionCodeChange -> {
                val sessionCode = action.value.asSessionCode()
                _state.update {
                    it.copy(
                        sessionCode = sessionCode,
                        selectedParticipantName = "",
                        participantNameOptions = emptyList(),
                        isRosterLoading = sessionCode.length == SESSION_CODE_LENGTH,
                        error = null,
                    )
                }
                observeRoster(sessionCode)
            }

            is JoinSessionAction.OnParticipantNameSelect -> _state.update {
                it.copy(selectedParticipantName = action.value, error = null)
            }

            is JoinSessionAction.OnParticipantNamesChange -> _state.update {
                it.copy(
                    participantNamesDraft = action.value,
                    isParticipantNamesDirty = true,
                    error = null,
                )
            }

            is JoinSessionAction.OnQuestionChange -> _state.update {
                it.copy(
                    questionDrafts = it.questionDrafts.mapIndexed { index, question ->
                        if (index == action.index) action.value.asQuestionDraft() else question
                    },
                    error = null,
                )
            }

            is JoinSessionAction.OnQuestionTimerChange -> _state.update {
                it.copy(
                    questionTimerDrafts = it.questionTimerDrafts.mapIndexed { index, timer ->
                        if (index == action.index) action.value.asQuestionTimerDraft() else timer
                    },
                    error = null,
                )
            }

            is JoinSessionAction.OnQuestionTimerEnabledChange -> _state.update {
                it.copy(
                    questionTimerEnabled = it.questionTimerEnabled.mapIndexed { index, enabled ->
                        if (index == action.index) action.enabled else enabled
                    },
                    error = null,
                )
            }

            is JoinSessionAction.OnQuestionWidgetChange -> _state.update {
                val defaultQuestions = QuestionWidget.entries.mapNotNull { widget -> widget.defaultQuestion }
                it.copy(
                    questionWidgets = it.questionWidgets.mapIndexed { index, widget ->
                        if (index == action.index) action.widget else widget
                    },
                    questionDrafts = it.questionDrafts.mapIndexed { index, question ->
                        val replaceable = question.isBlank() || question in defaultQuestions
                        if (index == action.index && replaceable) {
                            action.widget.defaultQuestion ?: question
                        } else {
                            question
                        }
                    },
                    error = null,
                )
            }

            is JoinSessionAction.OnQuestionAnswerVisibilityChange -> _state.update {
                it.copy(
                    questionAnswerVisibility = it.questionAnswerVisibility.mapIndexed { index, showAll ->
                        if (index == action.index) action.showAll else showAll
                    },
                    error = null,
                )
            }

            is JoinSessionAction.OnAnswerProcessedChange -> _state.update {
                it.copy(
                    processedAnswerIds = it.processedAnswerIds.toMutableSet().apply {
                        if (action.processed) add(action.answerId) else remove(action.answerId)
                    },
                )
            }

            is JoinSessionAction.OnAnswerChange -> _state.update {
                it.copy(answerDraft = action.value.asAnswerDraft(), error = null)
            }

            is JoinSessionAction.OnAnswerCategoryChange -> _state.update {
                it.copy(answerCategoryDraft = action.category, error = null)
            }

            is JoinSessionAction.OnAnswerChoiceChange -> if (_state.value.canSetAnswerChoice) {
                command {
                    remoteDataSource.setAnswerChoice(requireNotNull(_state.value.access), action.choice)
                }
            }

            is JoinSessionAction.OnEditAnswerClick -> _state.update { state ->
                val answer = state.session?.ownAnswers?.firstOrNull { it.id == action.answerId }
                if (answer == null || !state.canModifyOwnAnswers) {
                    state
                } else {
                    state.editing(answer)
                }
            }

            is JoinSessionAction.OnCurrentTimerChange -> _state.update {
                it.copy(currentTimerDraft = action.value.asQuestionTimerDraft(), error = null)
            }

            is JoinSessionAction.OnDeleteAnswerClick -> {
                val state = _state.value
                if (state.canModifyOwnAnswers && state.session?.ownAnswers?.any { it.id == action.answerId } == true) {
                    command(
                        request = {
                            remoteDataSource.deleteAnswer(requireNotNull(_state.value.access), action.answerId)
                        },
                        onSuccess = {
                            if (it.editingAnswerId == action.answerId) {
                                it.copy(editingAnswerId = null, answerDraft = "")
                            } else {
                                it
                            }
                        },
                    )
                }
            }

            JoinSessionAction.OnCancelAnswerEditClick -> _state.update {
                it.copy(editingAnswerId = null, answerDraft = "", error = null)
            }

            JoinSessionAction.OnJoinClick -> if (_state.value.canJoin) submit {
                remoteDataSource.joinSession(_state.value.sessionCode, _state.value.selectedParticipantName)
            }

            JoinSessionAction.OnCreateRetroClick -> if (_state.value.displayName.isBlank()) {
                _state.update { it.copy(error = JoinSessionError.NAME_REQUIRED) }
            } else {
                submit { remoteDataSource.createSession(_state.value.displayName) }
            }

            JoinSessionAction.OnAddQuestionClick -> if (_state.value.canAddQuestion) {
                _state.update {
                    it.copy(
                        questionDrafts = it.questionDrafts + "",
                        questionTimerDrafts = it.questionTimerDrafts +
                            (DEFAULT_QUESTION_DURATION_SECONDS / 60).toString(),
                        questionTimerEnabled = it.questionTimerEnabled + false,
                        questionWidgets = it.questionWidgets + QuestionWidget.TEXT,
                        questionAnswerVisibility = it.questionAnswerVisibility + false,
                        questionVoteSources = it.questionVoteSources + null,
                        questionVoteBudgets = it.questionVoteBudgets + DEFAULT_VOTES_PER_PARTICIPANT,
                    )
                }
            }

            JoinSessionAction.OnSaveParticipantNamesClick -> if (_state.value.canSaveParticipantNames) {
                command(
                    request = {
                        remoteDataSource.updateParticipantNames(
                            requireNotNull(_state.value.access),
                            _state.value.participantNamesDraft.asParticipantNames(),
                        )
                    },
                    onSuccess = { it.copy(isParticipantNamesDirty = false) },
                )
            }

            JoinSessionAction.OnStartRetroClick -> if (_state.value.canStartRetro) command {
                remoteDataSource.startRetro(requireNotNull(_state.value.access), _state.value.startRetroQuestions())
            }

            is JoinSessionAction.OnQuestionVoteSourceChange -> _state.update {
                it.copy(questionVoteSources = it.questionVoteSources.replaced(action.index, action.source))
            }

            is JoinSessionAction.OnQuestionVoteBudgetChange -> _state.update {
                it.copy(questionVoteBudgets = it.questionVoteBudgets.replaced(action.index, action.budget))
            }

            is JoinSessionAction.OnClusterModeChange -> _state.update {
                it.copy(isClustering = action.enabled, clusterSelection = emptySet(), clusterNameDraft = "")
            }

            is JoinSessionAction.OnClusterAnswerToggle -> _state.update {
                it.copy(
                    clusterSelection = if (action.answerId in it.clusterSelection) {
                        it.clusterSelection - action.answerId
                    } else {
                        it.clusterSelection + action.answerId
                    },
                )
            }

            is JoinSessionAction.OnClusterNameChange -> _state.update {
                it.copy(clusterNameDraft = action.value.take(MAX_CLUSTER_NAME_LENGTH))
            }

            JoinSessionAction.OnCreateClusterClick -> _state.value
                .clustersWithNewCluster("t" + Random.nextLong().toULong().toString(36))
                ?.let { clusters ->
                    command(
                        request = { remoteDataSource.updateClusters(requireNotNull(_state.value.access), clusters) },
                        onSuccess = { it.copy(clusterSelection = emptySet(), clusterNameDraft = "") },
                    )
                }

            is JoinSessionAction.OnDeleteClusterClick -> _state.value.session?.let { session ->
                command {
                    remoteDataSource.updateClusters(
                        requireNotNull(_state.value.access),
                        session.clusters.without(action.clusterId),
                    )
                }
            }

            JoinSessionAction.OnStartCurrentQuestionTimerClick -> if (_state.value.canStartCurrentQuestionTimer) {
                command(
                    request = {
                        remoteDataSource.startCurrentQuestionTimer(
                            requireNotNull(_state.value.access),
                            _state.value.currentTimerDraft.toInt() * 60,
                        )
                    },
                    onSuccess = { it.copy(currentTimerDraft = "") },
                )
            }

            is JoinSessionAction.OnCurrentQuestionAnswerVisibilityChange -> command {
                remoteDataSource.setCurrentQuestionAnswerVisibility(
                    requireNotNull(_state.value.access),
                    action.showAll,
                )
            }

            JoinSessionAction.OnSubmitAnswerClick -> if (_state.value.canSubmitAnswer) {
                val answerId = _state.value.editingAnswerId
                val content = requireNotNull(_state.value.answerContent)
                command(
                    request = {
                        if (answerId == null) {
                            remoteDataSource.submitAnswer(requireNotNull(_state.value.access), content)
                        } else {
                            remoteDataSource.updateAnswer(requireNotNull(_state.value.access), answerId, content)
                        }
                    },
                    onSuccess = { it.copy(answerDraft = "", editingAnswerId = null) },
                )
            }

            JoinSessionAction.OnRevealAnswersClick -> command {
                remoteDataSource.revealAnswers(requireNotNull(_state.value.access))
            }

            JoinSessionAction.OnNextQuestionClick -> command {
                remoteDataSource.nextQuestion(requireNotNull(_state.value.access))
            }

            JoinSessionAction.OnDownloadRetroClick -> _state.value.session?.let { session ->
                _events.trySend(JoinSessionEvent.DownloadMarkdown(session.markdownFileName, session.toMarkdown()))
            }
            JoinSessionAction.OnShareRetroClick -> _state.value.session?.let { session ->
                _events.trySend(JoinSessionEvent.ShareMarkdown(session.markdownFileName, session.toMarkdown()))
            }
            JoinSessionAction.OnLeaveLobbyClick -> leaveLobby(
                showSummary = _state.value.access?.role == ParticipantRole.MODERATOR,
            )
            JoinSessionAction.OnSummaryDoneClick -> if (_state.value.session?.isEnded == true) {
                resetToJoin(clearSessionCode = true)
            } else {
                leaveLobby(showSummary = false)
            }
            JoinSessionAction.OnSessionEndedConfirmClick -> resetToJoin(clearSessionCode = true)
        }
    }

    private fun submit(request: suspend () -> RetroApiResult<SessionAccess>) {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            when (val result = request()) {
                is RetroApiResult.Success -> openSession(result.value)
                is RetroApiResult.Error -> _state.update {
                    it.copy(isLoading = false, error = result.reason.toUiError())
                }
            }
        }
    }

    private fun command(
        onSuccess: (JoinSessionState) -> JoinSessionState = { it },
        request: suspend () -> RetroApiResult<Unit>,
    ) {
        if (_state.value.access == null || _state.value.isLoading) return
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            when (val result = request()) {
                is RetroApiResult.Success -> _state.update { onSuccess(it).copy(isLoading = false) }
                is RetroApiResult.Error -> _state.update {
                    it.copy(isLoading = false, error = result.reason.toActionUiError())
                }
            }
        }
    }

    private fun openSession(access: SessionAccess) {
        sessionAccessStorage.save(access)
        rosterJob?.cancel()
        rosterJob = null
        _state.update {
            it.copy(
                screen = access.screenFor(RetroPhase.LOBBY),
                sessionCode = access.sessionCode,
                isLoading = false,
                access = access,
            )
        }
        lobbyJob?.cancel()
        timerJob?.cancel()
        timerJob = viewModelScope.launch {
            while (true) {
                delay(1_000L)
                _state.update {
                    val remainingSeconds = it.questionRemainingSeconds
                    if (remainingSeconds != null && remainingSeconds > 0) {
                        it.copy(questionRemainingSeconds = remainingSeconds - 1)
                    } else {
                        it
                    }
                }
            }
        }
        lobbyJob = viewModelScope.launch {
            try {
                when (val result = remoteDataSource.loadSession(access)) {
                    is RetroApiResult.Success -> showSession(access, result.value)
                    is RetroApiResult.Error -> {
                        if (result.reason == RetroApiError.SESSION_NOT_FOUND ||
                            result.reason == RetroApiError.UNAUTHORIZED
                        ) {
                            resetToJoin()
                            _state.update { it.copy(error = result.reason.toUiError()) }
                            return@launch
                        }
                        _state.update { it.copy(isLoading = false, error = result.reason.toUiError()) }
                    }
                }
                remoteDataSource.session(access).collect { session -> showSession(access, session) }
            } catch (exception: CancellationException) {
                throw exception
            } catch (_: Exception) {
                _state.update { it.copy(error = JoinSessionError.CONNECTION) }
            }
        }
    }

    private fun showSession(access: SessionAccess, session: SessionSnapshot) {
        _state.update {
            if ((session.isEnded || session.phase == RetroPhase.FINISHED) &&
                access.role == ParticipantRole.PARTICIPANT
            ) {
                return@update it.copy(
                    session = session,
                    screen = RetroScreen.PARTICIPANT_LOBBY,
                    isLoading = false,
                    showSessionEndedDialog = true,
                    error = null,
                )
            }
            val questionChanged = it.session?.currentQuestion?.id != session.currentQuestion?.id
            it.copy(
                session = session,
                screen = if (session.isEnded && access.role == ParticipantRole.MODERATOR) {
                    RetroScreen.MODERATOR_SUMMARY
                } else {
                    access.screenFor(session.phase)
                },
                answerDraft = if (questionChanged) "" else it.answerDraft,
                answerCategoryDraft = if (questionChanged) null else it.answerCategoryDraft,
                isClustering = it.isClustering && !questionChanged,
                clusterSelection = if (questionChanged) emptySet() else it.clusterSelection,
                clusterNameDraft = if (questionChanged) "" else it.clusterNameDraft,
                editingAnswerId = if (questionChanged) null else it.editingAnswerId,
                currentTimerDraft = if (questionChanged) "" else it.currentTimerDraft,
                processedAnswerIds = if (questionChanged) emptySet() else it.processedAnswerIds,
                participantNamesDraft = if (it.isParticipantNamesDirty) {
                    it.participantNamesDraft
                } else {
                    session.participantNames.joinToString("\n")
                },
                questionRemainingSeconds = session.questionRemainingSeconds,
                isLoading = false,
                error = null,
            )
        }
    }

    private fun leaveLobby(showSummary: Boolean) {
        val access = _state.value.access ?: return
        if (_state.value.isLoading) return
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true, error = null) }
            when (val result = remoteDataSource.leaveSession(access)) {
                is RetroApiResult.Success -> if (showSummary && result.value != null) {
                    showSession(access, result.value)
                } else {
                    resetToJoin(clearSessionCode = true)
                }
                is RetroApiResult.Error -> _state.update {
                    it.copy(isLoading = false, error = result.reason.toActionUiError())
                }
            }
        }
    }

    private fun resetToJoin(
        previous: JoinSessionState = _state.value,
        clearSessionCode: Boolean = false,
    ) {
        lobbyJob?.cancel()
        lobbyJob = null
        timerJob?.cancel()
        timerJob = null
        sessionAccessStorage.clear()
        val sessionCode = previous.sessionCode.takeUnless { clearSessionCode }.orEmpty()
        _state.value = JoinSessionState(displayName = previous.displayName, sessionCode = sessionCode)
        observeRoster(sessionCode)
    }

    private fun observeRoster(sessionCode: String) {
        rosterJob?.cancel()
        rosterJob = null
        if (sessionCode.length != SESSION_CODE_LENGTH || _state.value.access != null) return
        rosterJob = viewModelScope.launch {
            when (val result = remoteDataSource.loadRoster(sessionCode)) {
                is RetroApiResult.Success -> showRoster(result.value)
                is RetroApiResult.Error -> {
                    _state.update { it.copy(isRosterLoading = false, error = result.reason.toUiError()) }
                    if (result.reason == RetroApiError.SESSION_NOT_FOUND) return@launch
                }
            }
            remoteDataSource.roster(sessionCode).collect { roster ->
                showRoster(roster)
            }
        }
    }

    private fun showRoster(roster: SessionRoster) {
        _state.update { state ->
            val selectedName = state.selectedParticipantName.takeIf { selected ->
                roster.names.any { it.name == selected && !it.isSelected }
            }.orEmpty()
            state.copy(
                participantNameOptions = if (roster.isEnded) emptyList() else roster.names,
                selectedParticipantName = selectedName,
                isRosterLoading = false,
                error = if (roster.isEnded) JoinSessionError.SESSION_NOT_FOUND else null,
            )
        }
    }

    override fun onCleared() {
        remoteDataSource.close()
        super.onCleared()
    }
}

private fun RetroApiError.toUiError() = when (this) {
    RetroApiError.INVALID_INPUT -> JoinSessionError.NAME_REQUIRED
    RetroApiError.SESSION_NOT_FOUND -> JoinSessionError.SESSION_NOT_FOUND
    RetroApiError.UNAUTHORIZED, RetroApiError.INVALID_STATE -> JoinSessionError.ACTION_REJECTED
    RetroApiError.NAME_UNAVAILABLE -> JoinSessionError.NAME_UNAVAILABLE
    RetroApiError.RATE_LIMITED -> JoinSessionError.RATE_LIMITED
    RetroApiError.CONNECTION -> JoinSessionError.CONNECTION
}

private fun RetroApiError.toActionUiError() = when (this) {
    RetroApiError.CONNECTION -> JoinSessionError.CONNECTION
    RetroApiError.NAME_UNAVAILABLE -> JoinSessionError.NAME_UNAVAILABLE
    RetroApiError.RATE_LIMITED -> JoinSessionError.RATE_LIMITED
    else -> JoinSessionError.ACTION_REJECTED
}

internal fun SessionAccess.screenFor(phase: RetroPhase) = when {
    phase == RetroPhase.LOBBY && role == ParticipantRole.MODERATOR -> RetroScreen.MODERATOR_LOBBY
    phase == RetroPhase.LOBBY -> RetroScreen.PARTICIPANT_LOBBY
    phase == RetroPhase.FINISHED && role == ParticipantRole.MODERATOR -> RetroScreen.MODERATOR_SUMMARY
    phase == RetroPhase.FINISHED -> RetroScreen.PARTICIPANT_LOBBY
    role == ParticipantRole.MODERATOR -> RetroScreen.MODERATOR_QUESTION
    else -> RetroScreen.PARTICIPANT_QUESTION
}

private fun <T> List<T>.replaced(index: Int, value: T) = mapIndexed { current, item -> if (current == index) value else item }
